import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from './AuthProvider';
import logger from '../utils/logger';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  fallbackPath = '/login',
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth state is being determined
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    logger.authEvent('access_denied_unauthenticated', {
      attempted_path: location.pathname,
      redirect_to: fallbackPath,
    });

    return (
      <Navigate
        to={fallbackPath}
        state={{ returnUrl: location.pathname + location.search }}
        replace
      />
    );
  }

  // Check role-based authorization if required roles are specified
  if (requiredRoles.length > 0 && user) {
    const userRoles = getUserRoles(user);
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));

    if (!hasRequiredRole) {
      logger.authEvent('access_denied_insufficient_roles', {
        user_id: user.profile.sub,
        user_roles: userRoles,
        required_roles: requiredRoles,
        attempted_path: location.pathname,
      });

      return (
        <Navigate
          to="/access-denied"
          state={{ 
            requiredRoles,
            userRoles,
            attemptedPath: location.pathname 
          }}
          replace
        />
      );
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

/**
 * Extract user roles from the OIDC token claims
 */
function getUserRoles(user: any): string[] {
  const roles: string[] = [];

  // Check realm-level roles
  if (user.profile?.realm_access?.roles) {
    roles.push(...user.profile.realm_access.roles);
  }

  // Check client-level roles
  if (user.profile?.resource_access) {
    Object.values(user.profile.resource_access).forEach((clientAccess: any) => {
      if (clientAccess?.roles) {
        roles.push(...clientAccess.roles);
      }
    });
  }

  // Check for custom role claims
  if (user.profile?.roles) {
    if (Array.isArray(user.profile.roles)) {
      roles.push(...user.profile.roles);
    } else if (typeof user.profile.roles === 'string') {
      roles.push(user.profile.roles);
    }
  }

  // Remove duplicates and return
  return Array.from(new Set(roles));
}

/**
 * Hook to check if user has specific roles
 */
export const useHasRole = (requiredRoles: string | string[]): boolean => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return false;
  }

  const userRoles = getUserRoles(user);
  const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  return rolesToCheck.some(role => userRoles.includes(role));
};

/**
 * Hook to get current user's roles
 */
export const useUserRoles = (): string[] => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return [];
  }

  return getUserRoles(user);
};

/**
 * Component that conditionally renders based on user roles
 */
interface RoleGuardProps {
  children: ReactNode;
  requiredRoles: string | string[];
  fallback?: ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredRoles,
  fallback = null,
}) => {
  const hasRole = useHasRole(requiredRoles);
  return hasRole ? <>{children}</> : <>{fallback}</>;
};

export default ProtectedRoute;